import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { TrendingUp, Users, Clock } from 'lucide-react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import AnalyticsKPICard from './components/AnalyticsKPICard';
import EngagementTrendChart from './components/EngagementTrendChart';
import PageEngagementTable from './components/PageEngagementTable';
import FeatureUsageHeatmap from './components/FeatureUsageHeatmap';
import BottleneckIdentification from './components/BottleneckIdentification';
import TrainingRecommendations from './components/TrainingRecommendations';
import AnalyticsFilters from './components/AnalyticsFilters';
import userAnalyticsService from '../../services/userAnalyticsService';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

const UserAnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: 'last_30_days',
    department: 'all',
    userRole: 'all'
  });
  const { companySettings } = useCompanySettings();

  const companyName = companySettings?.companyName || 'HR Management';

  const [statistics, setStatistics] = useState(null);
  const [engagementTrends, setEngagementTrends] = useState([]);
  const [featureAdoption, setFeatureAdoption] = useState([]);
  const [pageEngagement, setPageEngagement] = useState([]);
  const [bottlenecks, setBottlenecks] = useState([]);
  const [trainingRecommendations, setTrainingRecommendations] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadAnalyticsData();
    checkSubscription();
  }, [filters]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [stats, trends, adoption, engagement, bottleneckData, recommendations] = await Promise.all([
        userAnalyticsService?.getDashboardStatistics(filters?.dateFrom, filters?.dateTo),
        userAnalyticsService?.getEngagementTrends(filters?.dateFrom, filters?.dateTo),
        userAnalyticsService?.getFeatureAdoptionMatrix(),
        userAnalyticsService?.getPageEngagementMetrics(),
        userAnalyticsService?.getWorkflowBottlenecks({ resolved: false }),
        userAnalyticsService?.getTrainingRecommendations({ status: 'pending' })
      ]);

      setStatistics(stats);
      setEngagementTrends(trends);
      setFeatureAdoption(adoption);
      setPageEngagement(engagement);
      setBottlenecks(bottleneckData);
      setTrainingRecommendations(recommendations);
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleExportReport = () => {
    const reportData = {
      generatedAt: new Date()?.toISOString(),
      period: `${filters?.dateFrom} to ${filters?.dateTo}`,
      statistics,
      engagementTrends,
      featureAdoption,
      pageEngagement,
      bottlenecks,
      trainingRecommendations
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `user-analytics-report-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <ComplianceAlertBanner />
        <LegalDisclaimerBanner />
        <GlobalFilterBar onFilterChange={handleFilterChange} />
        <DataRefreshIndicator />
        <main className="pt-32 pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading analytics data...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <ComplianceAlertBanner />
        <LegalDisclaimerBanner />
        <GlobalFilterBar onFilterChange={handleFilterChange} />
        <DataRefreshIndicator />
        <main className="pt-32 pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-error/10 border border-error/20 rounded-lg p-6 text-center">
              <p className="text-error font-medium mb-2">Error Loading Analytics</p>
              <p className="text-muted-foreground text-sm">{error}</p>
              <button
                onClick={loadAnalyticsData}
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth"
              >
                Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>User Analytics Dashboard - {companyName}</title>
        <meta name="description" content="Comprehensive insights into HR system usage patterns and team productivity optimization with user adoption tracking and bottleneck identification" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <ComplianceAlertBanner />
        <LegalDisclaimerBanner />
        <GlobalFilterBar onFilterChange={handleFilterChange} />
        <DataRefreshIndicator />

        <main className="pt-32 pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-3">
                    User Analytics Dashboard
                  </h1>
                  <p className="text-base md:text-lg text-muted-foreground max-measure">
                    Comprehensive insights into HR system usage patterns and team productivity optimization
                  </p>
                </div>
                <button
                  onClick={handleExportReport}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth flex items-center gap-2"
                >
                  <span>Export Report</span>
                </button>
              </div>
            </div>

            <AnalyticsFilters filters={filters} onChange={handleFilterChange} />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
              <AnalyticsKPICard
                title="Daily Active Users"
                value={statistics?.dailyActiveUsers || 0}
                subtitle={`${statistics?.totalSessions || 0} sessions`}
                icon="Users"
                trend={12.5}
                status="compliant"
              />
              <AnalyticsKPICard
                title="Feature Adoption Rate"
                value={`${statistics?.featureAdoptionRate || 0}%`}
                subtitle="of available features"
                icon="TrendingUp"
                trend={8.3}
                status="compliant"
              />
              <AnalyticsKPICard
                title="Avg Session Duration"
                value={`${statistics?.avgSessionDuration || 0}m`}
                subtitle="per user session"
                icon="Clock"
                trend={-5.2}
                status="warning"
              />
              <AnalyticsKPICard
                title="Productivity Score"
                value={`${statistics?.productivityScore || 0}%`}
                subtitle="successful actions"
                icon="Activity"
                trend={15.7}
                status="compliant"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
              <div className="lg:col-span-8">
                <EngagementTrendChart data={engagementTrends} />
              </div>
              <div className="lg:col-span-4">
                <BottleneckIdentification bottlenecks={bottlenecks} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <FeatureUsageHeatmap data={featureAdoption} />
              <PageEngagementTable data={pageEngagement} />
            </div>

            <TrainingRecommendations recommendations={trainingRecommendations} />
          </div>
        </main>
      </div>
      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </React.Fragment>
  );
};

export default UserAnalyticsDashboard;